import { useCallback, useEffect, useState } from "react";

import { useAuth } from "../../../contexts/auth2";
import { handlePromise } from "../../../utils/handlePromisse";
import { getRanking } from "../../../services/userService";
import { getUserProfile, UserProfileResponse } from "../../../services/profileService";
import { getPatientsPaginated2, Patient } from "../../../services/patient.service";

// ----------------------------------------------------------------------

export interface RecentConsultation {
  id: number;
  type: string;
  patientName: string;
  focus?: string;
  date: string;
  status?: 'COMPLETED' | 'DRAFT';
}

export interface RankingStats {
  position: number;
  formulas: number;
  patients: number;
  /** Prompt #3 — variação de posição nos últimos 30 dias (positivo = subiu) */
  positionDelta30d?: number;
  /** Prompt #3 — total de formuladoras no ranking */
  totalUsers?: number;
  /** Prompt #3 — percentual de formuladoras atrás do usuário */
  percentileAhead?: number;
  /** Prompt #3 — fórmulas restantes para atingir o próximo tier */
  formulasToNextTier?: number;
  /** Prompt #3 — rótulo do próximo tier (ex.: "TOP 10") */
  nextTierLabel?: string;
}

export interface DashboardData {
  loading: boolean;
  error: string | null;
  profile: UserProfileResponse | null;
  ranking: RankingStats | null;
  recentConsultations: RecentConsultation[];
}

/** Achata as consultas dos pacientes, ordena por data desc e limita */
export function flattenRecentConsultations(patients: Patient[], limit = 4): RecentConsultation[] {
  return patients
    .flatMap((patient) =>
      (patient.consultations ?? []).map((consultation) => ({
        id: consultation.id,
        type: consultation.type,
        patientName: patient.name,
        focus: consultation.name,
        date: consultation.date,
        status: consultation.status,
      }))
    )
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, limit);
}

export default function useDashboardData(): DashboardData {
  const { user } = useAuth();
  const [data, setData] = useState<DashboardData>({
    loading: true,
    error: null,
    profile: null,
    ranking: null,
    recentConsultations: [],
  });

  const load = useCallback(async () => {
    setData((prev) => ({ ...prev, loading: true, error: null }));

    const [[profileResult, profileError], [rankingResult, rankingError], [patientsResult]] = await Promise.all([
      handlePromise(getUserProfile(user.idUser)),
      handlePromise(getRanking(user.idUser)),
      handlePromise(getPatientsPaginated2({ page: 1, size: 20, order: "desc", orderBy: "consultationDate" })),
    ]);

    if (profileError && rankingError) {
      setData((prev) => ({ ...prev, loading: false, error: "Erro ao carregar o painel." }));
      return;
    }

    const ranking = rankingResult?.data;
    setData({
      loading: false,
      error: null,
      profile: profileResult?.data ?? null,
      ranking: ranking
        ? {
            position: ranking.ranking,
            formulas: ranking.countConsultation,
            patients: ranking.countPatient,
            ...(ranking.positionDelta30d != null ? { positionDelta30d: ranking.positionDelta30d } : {}),
            ...(ranking.totalUsers != null ? { totalUsers: ranking.totalUsers } : {}),
            ...(ranking.percentileAhead != null ? { percentileAhead: ranking.percentileAhead } : {}),
            ...(ranking.formulasToNextTier != null ? { formulasToNextTier: ranking.formulasToNextTier } : {}),
            ...(ranking.nextTierLabel != null ? { nextTierLabel: ranking.nextTierLabel } : {}),
          }
        : null,
      recentConsultations: flattenRecentConsultations(patientsResult?.data?.data ?? []),
    });
  }, [user.idUser]);

  useEffect(() => {
    load();
  }, [load]);

  return data;
}
